import { Link } from 'react-router-dom';
import { Heart, Calendar, ArrowRight, Check, Sun, Brain } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BOOK_HAIR_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const BOOK_BEAUTY_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const wellnessServices = [
  { 
    name: 'Relaxation Massage', 
    description: 'Gentle, soothing massage to ease tension and promote deep relaxation', 
    price: '$135', 
    duration: '60 min'
  },
  { 
    name: 'Remedial Massage', 
    description: 'Deep tissue & targeted muscle release for chronic pain and postural knots', 
    price: '$145', 
    duration: '60 min',
    note: '30 min - $80'
  },
  { 
    name: 'Oncology Massage', 
    description: 'Specialized gentle touch care. Safe comfort for cancer patients; eases fatigue and nausea', 
    price: '$195', 
    duration: '60 min',
    day: 'Fridays'
  },
  { 
    name: 'Lymphatic Drainage Massage', 
    description: 'Gentle rhythmic "pumping" strokes. Reduces swelling, detoxes the body, and helps healing', 
    price: '$195', 
    duration: '60 min'
  },
  { 
    name: 'Menopause & Peri-Menopause Wellness Coaching', 
    description: '1-on-1 holistic consultation. Manage hot flushes, brain fog, and hormonal shifts', 
    price: '$120', 
    duration: '60 min',
    day: 'Fridays'
  },
];

const comingSoon = [
  { 
    name: 'Infrared Body Therapy', 
    description: 'Full body rejuvenation using infrared light technology',
    icon: '🔴'
  },
  { 
    name: 'LED Light Body Therapy', 
    description: 'Coming soon to our cozy waiting area — full body cellular rejuvenation',
    icon: '✨'
  },
];

const serviceFeatures = [
  {
    service: 'Remedial Massage',
    features: 'Deep tissue & muscle release',
    benefits: 'Relieves chronic pain and fixes postural knots'
  },
  {
    service: 'Oncology Massage',
    features: 'Specialized gentle touch care',
    benefits: 'Safe comfort for cancer patients; eases fatigue/nausea'
  },
  {
    service: 'Lymphatic Drainage',
    features: 'Gentle rhythmic "pumping" strokes',
    benefits: 'Reduces swelling, detoxes the body, and helps healing'
  },
  {
    service: 'Menopause Coaching',
    features: 'Holistic 1-on-1 consultation',
    benefits: 'Manage hot flushes, brain fog, and hormonal shifts'
  },
];

const benefits = [
  'Reduce stress and anxiety',
  'Relieve chronic pain',
  'Improve sleep quality',
  'Boost immune system',
  'Reduce swelling and inflammation',
  'Support through life transitions',
];

export default function WellnessServices() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="wellness-services-page">
      <SEO page="wellness" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32" data-testid="wellness-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1920&h=800&fit=crop"
            alt="Wellness treatment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-6">
              <Heart className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">🌿 Body & Wellbeing</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Wellness Services
            </h1>
            <p className="text-lg text-[#2C2420] leading-relaxed mb-8">
              Precision care for the human form. Specialized massage, coaching, and holistic support for your wellbeing journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOK_BEAUTY_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-lg"
                  data-testid="wellness-book-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Beauty & Wellness
                </Button>
              </a>
              <a href={BOOK_HAIR_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline"
                  className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg"
                >
                  Book Hair
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Friday Special */}
      <section className="py-8 bg-[#8D7B68]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <span className="text-white/80">🌿 THE FRIDAY RESET:</span>
            <span className="text-white font-medium">Book Remedial Massage + Waterfall Head Spa together for $210 (Save $35)</span>
          </div>
        </div>
      </section>

      {/* Wellness Services */}
      <section className="py-24 md:py-32" data-testid="wellness-services-list">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">The Michael Angelo Suite</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-4">Body & Healing</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Precision care for the human form. Remedial massage, oncology support, and holistic coaching.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {wellnessServices.map((service) => (
              <div 
                key={service.name}
                className="p-6 bg-white rounded-lg border border-[#D4C4B0]/50 hover:border-[#8D7B68]/50 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-lg font-medium text-[#2C2420]">{service.name}</h4>
                  <p className="text-[#8D7B68] font-bold ml-4 whitespace-nowrap">{service.price}</p>
                </div>
                <p className="text-sm text-[#5D4A3A] mb-3">{service.description}</p>
                <div className="flex gap-4 text-xs text-[#6B5D4D]">
                  <span>⏱ {service.duration}</span>
                  {service.note && <span className="text-[#8D7B68]">• {service.note}</span>}
                  {service.day && <span className="text-[#8D7B68] font-medium">📅 {service.day}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href={BOOK_BEAUTY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4">
                Book Your Appointment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="py-24 md:py-32 bg-[#D4C4B0]/20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Why Choose Our Wellness</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Benefits of Our Treatments
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Our wellness treatments offer specialized care that goes beyond relaxation. Experience medical-grade support for your body and mind.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3 text-[#3D352D]">
                    <div className="w-6 h-6 rounded-full bg-[#D4C4B0]/50 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#8D7B68]" />
                    </div>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Features Table */}
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg overflow-hidden text-sm">
                <thead className="bg-[#8D7B68] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Service</th>
                    <th className="px-4 py-3 text-left font-medium">What it is</th>
                    <th className="px-4 py-3 text-left font-medium">Why you need it</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceFeatures.map((item, index) => (
                    <tr key={item.service} className={index % 2 === 0 ? 'bg-white' : 'bg-[#D4C4B0]/10'}>
                      <td className="px-4 py-3 font-medium text-[#2C2420]">{item.service}</td>
                      <td className="px-4 py-3 text-[#5D4A3A]">{item.features}</td>
                      <td className="px-4 py-3 text-[#5D4A3A]">{item.benefits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-24 md:py-32" data-testid="wellness-coming-soon">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#8D7B68] text-white rounded-full px-4 py-2 mb-4">
              <span className="text-sm font-medium">🌻 The Wellbeing Suite — Coming Soon</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Coming Soon</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              We're expanding our wellness offerings with cutting-edge light therapy treatments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {comingSoon.map((service) => (
              <div 
                key={service.name}
                className="p-8 bg-white rounded-lg border border-[#D4C4B0]/50 text-center relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-medium text-[#2C2420] mb-2">{service.name}</h3>
                <p className="text-sm text-[#5D4A3A]">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Begin Your Wellness Journey
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Take the first step towards holistic wellbeing. Book a wellness session today and experience the healing power of specialized care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BOOK_BEAUTY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Book Beauty & Wellness
              </Button>
            </a>
            <a href={BOOK_HAIR_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#2C2420] rounded-full px-10 py-6 text-lg">
                Book Hair
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
